import { useAppDispatch, useAppSelector } from "../../store/store";
import {
    deliveryProvidersSelector,
    selectedDeliveryProviderSelector,
    selectedSectorSelector,
    setDeliveryProvider,
    languageSelector,
} from "../../service/common.slice";
import "./DeliveryModeSelector.sass";
import { DeliveryProviderFragment } from "../../service/__generated__/DeliveryProviderFragment";
import {
    getDeliveryComment,
    getDeliveryName,
    useLg,
    useLoading,
} from "../../service/utils";
import { CheckInput } from "../../common/contols/CheckInput";
import useId from "@mui/utils/useId";
import React, { ReactNode, useCallback, useEffect, useMemo } from "react";


export const DeliveryModeSelector = () => {
    const [loading, wl] = useLoading();
    const _ = useLg();
    const lang = useAppSelector(languageSelector);
    const dispatch = useAppDispatch();
    const selectedSector = useAppSelector(selectedSectorSelector)!;
    const selectedDeliveryProvider = useAppSelector(selectedDeliveryProviderSelector);
    const selectedDeliveryProviderId = selectedDeliveryProvider?._id;
    const allDeliveryProviders = useAppSelector(deliveryProvidersSelector);
    // Ordering is owned by the backend (sortKey, else type grouping); the client
    // just renders active providers in the order it received them.
    const deliveryProviders = useMemo(
        () => allDeliveryProviders.filter(p => p.isActive),
        [allDeliveryProviders]
    );

    const setProvider = useCallback(async (provider: DeliveryProviderFragment) => {
        if (wl.loading) return;
        await wl.load(dispatch(setDeliveryProvider(provider)));
    }, [dispatch, wl]);

    const deliveryOptions = useMemo(()=>{
        const checkboxes: ReactNode[] = [];

        deliveryProviders.forEach(deliveryProvider => {
            const isProviderSelected = selectedDeliveryProviderId===deliveryProvider._id;
            
            checkboxes.push(
                <CheckboxRow
                    label={getDeliveryName(deliveryProvider)(lang)}
                    comment={selectedSector ? getDeliveryComment(deliveryProvider, selectedSector)(lang) : ""}
                    checked={isProviderSelected}
                    onChecked={()=>setProvider(deliveryProvider)}
                />
            );
        })

        return checkboxes;
    }, [
        selectedDeliveryProviderId,
        deliveryProviders,
        selectedSector,
        setProvider,
        lang,
    ]);
    
    useEffect(()=>{
        if (deliveryOptions.length === 1) {
            const deliveryProvider = deliveryProviders.find(p=>p.isActive)!;
            setProvider(deliveryProvider);
        }
    }, [selectedSector, deliveryOptions, deliveryProviders, setProvider]);

    if (deliveryOptions.length === 0) {
        return <div className="">
            {_("Leider ist der Dienst derzeit nicht verfügbar")}
        </div>
    }

    return (
        <div className="box cart">
            {deliveryOptions.length > 1 && <h2 className="select-sector-header">{_("Bitte auswählen*")}</h2>}
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '15px' }}
                className={`with-loading-overlay ${loading?'loading':''}`}
            >
                <div className="loading-overlay">
                    <div className="icon icon-refresh" />
                </div>
                <div className="form-check-container">
                    {deliveryOptions.map((o,i)=>
                        <React.Fragment key={i}>
                            {o}
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
};


const CheckboxRow = ({label, checked, onChecked, comment}: 
    {label: string, checked: boolean, onChecked: VoidFunction, comment?: string}
) => {
    const id = useId();
    return <label>
        <div className={"form-check primary"}>
            <CheckInput
                className="form-check-input"
                type="radio"
                name="delivery_option"
                value={id}
                id={id}
                onChange={onChecked}
                checked={checked}
                disabled={false}
            />
            <div className="form-check-label">
                {label}
                {comment&&<div style={{ alignSelf: 'flex-end', fontSize: '10px' }}>
                    {comment}
                </div>}
            </div>
        </div>
        
    </label>
}
